import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { fetchPublicJobOpening } from "../../api/jobOpenings";
import {
  formatJobDate,
  isPublicJobOpening,
  publicJobOpeningContact,
  publicJobOtherContacts,
  requirementItemsFromJob,
  staticJobOpeningById,
} from "../../data/jobOpeningPublic";
import { JobApplicationForm } from "../../components/jobs/JobApplicationForm";
import { JobContactSections } from "../../components/jobs/JobContactSections";
import { JobApplyDock } from "../../components/jobs/JobApplyDock";
import { PageHeading } from "../../components/utils/pageHeading";
import { ContactCTA } from "../../components/home/ContactCTA";
import { ResourceLoading } from "../../components/utils/ResourceLoading";

function paragraphs(text) {
  if (!text) return [];
  return String(text)
    .split(/\n{2,}|\r\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function listLines(text) {
  if (!text) return [];
  return String(text)
    .split(/\n|\r\n/)
    .map((line) => line.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean);
}

export const StillingerPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    if (
      process.env.NODE_ENV !== "production" &&
      id === "forhandsvis-skjema"
    ) {
      setJob({
        id: 999,
        slug: "forhandsvis-skjema",
        title: "Servicetekniker",
        short_description: "Lokal forhåndsvisning av søknadsskjemaet.",
        description:
          "Dette er en forhåndsvisning. I prod kommer teksten fra Promang.",
        location: "Grålum",
        employment_type_label: "Heltid",
        contact: { name: "Christoffer Skaar", email: "cs@airon.no" },
        contact_name: "Christoffer Skaar",
        contact_email: "cs@airon.no",
        other_contacts: [],
        responsibles: [],
        website_form_enabled: true,
        requirement_items: [
          { id: "req_license", text: "Førerkort klasse B" },
          { id: "req_trade", text: "Fagbrev eller tilsvarende erfaring" },
          { id: "req_norwegian", text: "God skriftlig og muntlig norsk" },
        ],
      });
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await fetchPublicJobOpening(id);
        if (cancelled) return;
        if (isPublicJobOpening(res)) {
          setJob(res);
        } else {
          setJob(staticJobOpeningById(id));
        }
      } catch {
        if (!cancelled) setJob(staticJobOpeningById(id));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (loading) return;
    if (location.hash !== "#soknad") return;
    const timer = window.setTimeout(() => {
      document.getElementById("soknad")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [loading, location.hash, id]);

  const StaticContent = useMemo(() => job?._staticContent || null, [job]);

  if (loading) {
    return (
      <section className="sec">
        <div className="wrap">
          <ResourceLoading />
        </div>
      </section>
    );
  }

  if (!job) {
    return (
      <>
        <PageHeading
          eyebrow="Karriere"
          title="Fant ikke stillingen."
          description="Den kan være satt som utkast, lukket, eller adressen kan være feil."
        />
        <section className="sec tint" style={{ borderTop: "none" }}>
          <div className="wrap" style={{ textAlign: "center" }}>
            <Link to="/stillinger" className="btn">
              Tilbake til oversikt <span className="arr">→</span>
            </Link>
          </div>
        </section>
      </>
    );
  }

  const facts = [
    { label: "Sted", value: job.location },
    {
      label: "Ansettelsesform",
      value: job.employment_type_label || job.employment_type,
    },
    {
      label: "Stillingsprosent",
      value: job.work_percentage ? `${job.work_percentage} %` : null,
    },
    {
      label: "Antall",
      value: job.positions_count ? String(job.positions_count) : null,
    },
    { label: "Avdeling", value: job.department_name },
    { label: "Lønn", value: job.salary_info },
    { label: "Oppstart", value: formatJobDate(job.starts_on) },
    { label: "Søknadsfrist", value: formatJobDate(job.application_deadline) },
  ].filter((fact) => fact.value);

  const hasContact =
    !!publicJobOpeningContact(job) || publicJobOtherContacts(job).length > 0;

  return (
    <>
      <PageHeading
        eyebrow="Ledig stilling"
        title={job.title}
        description={job.short_description || ""}
      />

      <section className="sec tint" style={{ borderTop: "none" }}>
        <div className="wrap">
          <Link to="/stillinger" className="stilling-back">
            ← Alle stillinger
          </Link>

          <div className="stilling-detail-grid">
            <div>
              {StaticContent ? (
                <div className="stilling-static">
                  <StaticContent />
                </div>
              ) : (
                <>
                  {paragraphs(job.description).length > 0 && (
                    <Block title="Om stillingen">
                      {paragraphs(job.description).map((p) => (
                        <p key={p.slice(0, 40)} style={bodyStyle}>
                          {p}
                        </p>
                      ))}
                    </Block>
                  )}

                  {listLines(job.responsibilities).length > 0 && (
                    <Block title="Arbeidsoppgaver">
                      <ul style={listStyle}>
                        {listLines(job.responsibilities).map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </Block>
                  )}

              {requirementItemsFromJob(job).length > 0 && (
                <Block title="Kvalifikasjoner">
                  <ul style={listStyle}>
                    {requirementItemsFromJob(job).map((item) => (
                      <li key={item.id}>{item.text}</li>
                    ))}
                  </ul>
                </Block>
              )}

                  {listLines(job.benefits).length > 0 && (
                    <Block title="Vi tilbyr">
                      <ul style={listStyle}>
                        {listLines(job.benefits).map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </Block>
                  )}
                </>
              )}
            </div>

            <aside className="stilling-aside">
              {hasContact ? (
                <div className="stilling-panel">
                  <div className="stilling-contact is-aside-lead">
                    <JobContactSections job={job} titleStyle={asideTitleStyle} />
                  </div>
                </div>
              ) : null}

              {facts.length > 0 ? (
                <div className="stilling-panel">
                  <h2 style={asideTitleStyle}>Detaljer</h2>
                  <dl style={{ margin: 0 }}>
                    {facts.map((fact) => (
                      <div key={fact.label} className="stilling-fact">
                        <dt>{fact.label}</dt>
                        <dd>{fact.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ) : null}

              <div className="stilling-panel apply">
                <h2 style={{ ...asideTitleStyle, fontSize: 20 }}>
                  Søk på stillingen
                </h2>
                <p
                  style={{
                    margin: "0 0 16px",
                    color: "var(--ink-2)",
                    fontSize: 14,
                  }}
                >
                  Navn, e-post og CV er nok. Vi tar kontakt når vi har vurdert
                  søknaden.
                </p>
                <a href="#soknad" className="btn" style={{ width: "100%" }}>
                  Gå til søknad <span className="arr">→</span>
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="sec apply-section" id="soknad">
        <div className="wrap">
          <div className="apply-layout">
            <div>
              <div className="sec-tag">Søknad</div>
              <h2 className="apply-heading">
                Send søknad <em>på ett minutt.</em>
              </h2>
              <p className="apply-lead">
                Fyll inn navn og e-post, slipp CV-en hvis du har den klar, og
                send. Resten er valgfritt.
              </p>
              <JobApplicationForm job={job} />
            </div>
            <aside className="apply-help">
              <h3>Slik går det</h3>
              <ol>
                <li>Du sender søknaden herfra.</li>
                <li>Den lander i Promang hos Airon.</li>
                <li>Vi tar kontakt hvis det blir en prat.</li>
              </ol>
              <p>
                Trenger du hjelp? Ring oss eller send en mail — du snakker med
                noen som kjenner teamet.
              </p>
              {hasContact ? (
                <div className="stilling-contact">
                  <JobContactSections
                    job={job}
                    compact
                    titleStyle={{
                      fontFamily: "var(--display)",
                      fontSize: 16,
                      margin: "0 0 8px",
                    }}
                  />
                </div>
              ) : null}
            </aside>
          </div>
        </div>
      </section>

      <ContactCTA
        title="Spørsmål om stillingen"
        highlight="før du søker?"
        description="Ta kontakt — du snakker med noen som kjenner teamet og hverdagen i Airon."
        primary={{ label: "Kontakt oss", to: "/kontakt" }}
        secondary={{ label: "Alle stillinger", to: "/stillinger" }}
      />

      <JobApplyDock enabled />
    </>
  );
};

function Block({ title, children }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h2
        style={{
          fontFamily: "var(--display)",
          fontSize: 22,
          margin: "0 0 12px",
          color: "var(--ink)",
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

const bodyStyle = {
  margin: "0 0 12px",
  color: "var(--ink-2)",
  fontSize: 16,
  lineHeight: 1.65,
};

const listStyle = {
  margin: 0,
  paddingLeft: 18,
  color: "var(--ink-2)",
  fontSize: 16,
  lineHeight: 1.7,
};

const asideTitleStyle = {
  fontFamily: "var(--display)",
  fontSize: 18,
  margin: "0 0 14px",
};
